/**
 * Greg James Grand Prix - Retro F1 Racing Game
 *
 * Amiga-style pseudo-3D racer with on-screen Game Boy controls on mobile.
 * Follows the game.js pattern: checks for canvas, handles AJAX re-navigation.
 */

import { buildTrack, getTrackMinimapPoints } from './racing-track.js';
import {
  renderScene,
  renderHUD,
  renderTitleScreen,
  renderCountdown,
  loadCarSprites,
  loadRoadsideSprites,
} from './racing-renderer.js';

// Game states
const STATE = {
  TITLE: 'TITLE',
  COUNTDOWN: 'COUNTDOWN',
  RACING: 'RACING',
  FINISHED: 'FINISHED',
};

// Physics constants
const MAX_SPEED = 10000;
const ACCELERATION = 2000;
const BRAKING = 8000;
const NATURAL_DECEL = 2500;
const STEER_RATE = 1.5;
const CENTRIFUGAL = 0.5;
const OFFROAD_SPEED_MULT = 0.22;
const OFFROAD_DRAG = 6000;
const OFFROAD_MIN_SPEED = 30;
const TOTAL_LAPS = 3;

// Commentary messages
const COMMENTARY = {
  START: "LIGHTS OUT AND AWAY WE GO!",
  LAP_COMPLETE: ["WHAT A LAP!", "GREAT DRIVING!", "SMOOTH AS BUTTER!", "TEXTBOOK STUFF!"],
  BEST_LAP: "NEW FASTEST LAP!",
  OFFROAD: ["OFF INTO THE GRASS!", "KEEP IT ON THE TRACK!", "THAT'S THE SCENIC ROUTE!", "WATCH THE GRASS!"],
  FINAL_LAP: "LAST LAP! PUSH PUSH PUSH!",
  FINISH: "CHEQUERED FLAG! RACE COMPLETE!",
};

let gameBuilt = false;
let currentCanvas = null;

export default function racing() {
  const canvas = document.getElementById('racingCanvas');
  if (!canvas) return;

  // Reset if canvas changed (AJAX navigation)
  if (gameBuilt && currentCanvas !== canvas) {
    gameBuilt = false;
  }

  if (gameBuilt) return;
  currentCanvas = canvas;
  gameBuilt = true;

  const ctx = canvas.getContext('2d');
  const popUp = canvas.parentElement.querySelector('.pop-up');
  const startBtn = popUp ? popUp.querySelector('.start') : null;

  // Accessibility announcer
  let announcer = document.getElementById('racing-announcer');
  if (!announcer) {
    announcer = document.createElement('div');
    announcer.id = 'racing-announcer';
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'visually-hidden';
    document.body.appendChild(announcer);
  }

  // Build track
  const track = buildTrack();
  const minimapPoints = getTrackMinimapPoints();

  // Preload all sprites
  loadCarSprites();
  loadRoadsideSprites();

  // Game state
  let state = STATE.TITLE;
  let playerZ = 0;
  let playerX = 0; // -1 to 1 lateral
  let speed = 0;
  let lap = 1;
  let lapStartTime = 0;
  let currentLapTime = 0;
  let bestLapTime = Infinity;
  let lapTimes = [];
  let raceStartTime = 0;
  let totalRaceTime = 0;
  let countdownValue = 3;
  let countdownTimer = 0;
  let commentary = '';
  let commentaryTimer = 0;
  let titleScrollOffset = 0;
  let lastLapZ = 0;
  let isOffroad = false;
  let currentSteerInput = 0;
  let lateralSpeed = 0;
  let finishedTimer = 0;
  let finishedFade = 0;

  // Input state
  const keys = {};
  let touchGas = false;
  let touchBrake = false;
  let touchLeft = false;
  let touchRight = false;
  const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Canvas resize
  function resizeCanvas() {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  }
  resizeCanvas();
  window.addEventListener('resize', resizeCanvas);

  // On-screen control pad layout (Game Boy style: D-pad left, A/B right).
  // Returned coords are in canvas pixels and shared between hit-test and renderer.
  function getButtonLayout(w, h) {
    const sideMargin = 28;
    const bottomMargin = 30;
    const padW = 64;
    const padH = 56;
    const padGap = 6;
    const btnR = 38;
    const btnGap = 16;
    // L/R cluster (left side)
    const padY = h - bottomMargin - padH;
    const lX = sideMargin;
    const rX = sideMargin + padW + padGap;
    // A/B cluster (right side) — A is rightmost and slightly higher (Game Boy stagger)
    const aY = h - bottomMargin - btnR - 22;
    const aX = w - sideMargin - btnR;
    const bX = aX - btnR - btnGap - btnR;
    const bY = aY + 18;
    return {
      L: { kind: 'rect', x: lX, y: padY, w: padW, h: padH, label: '◀' }, // ◀
      R: { kind: 'rect', x: rX, y: padY, w: padW, h: padH, label: '▶' }, // ▶
      A: { kind: 'circle', x: aX, y: aY, r: btnR, label: 'A' },
      B: { kind: 'circle', x: bX, y: bY, r: btnR, label: 'B' },
    };
  }

  function hitTestButton(px, py) {
    const layout = getButtonLayout(canvas.width, canvas.height);
    for (const name of ['L', 'R', 'A', 'B']) {
      const b = layout[name];
      if (b.kind === 'rect') {
        if (px >= b.x && px <= b.x + b.w && py >= b.y && py <= b.y + b.h) return name;
      } else {
        const dx = px - b.x, dy = py - b.y;
        if (dx * dx + dy * dy <= b.r * b.r) return name;
      }
    }
    return null;
  }

  // Keyboard controls
  function onKeyDown(e) {
    keys[e.key] = true;
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(e.key)) {
      e.preventDefault();
    }
    if (state === STATE.FINISHED && finishedFade >= 1 && (e.key === 'Enter' || e.key === ' ')) {
      startRace();
    }
  }
  function onKeyUp(e) {
    keys[e.key] = false;
  }
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('keyup', onKeyUp);

  // Touch controls — on-screen Game Boy-style pad (L/R + A/B). Each touch
  // is hit-tested against the four buttons; sliding a finger between buttons
  // re-classifies it on touchmove so the controls stay forgiving.
  const activeTouches = new Map(); // trackingId → 'L' | 'R' | 'A' | 'B' | null

  function canvasCoords(t) {
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    return [(t.clientX - rect.left) * sx, (t.clientY - rect.top) * sy];
  }

  function classifyTouch(t) {
    const [px, py] = canvasCoords(t);
    return hitTestButton(px, py);
  }

  function updateTouchState() {
    touchLeft = false;
    touchRight = false;
    touchGas = false;
    touchBrake = false;
    activeTouches.forEach(v => {
      if (v === 'L') touchLeft = true;
      else if (v === 'R') touchRight = true;
      else if (v === 'A') touchGas = true;
      else if (v === 'B') touchBrake = true;
    });
  }

  function onTouchStart(e) {
    if (state !== STATE.RACING) return;
    let consumed = false;
    for (const t of e.changedTouches) {
      const hit = classifyTouch(t);
      activeTouches.set(t.identifier, hit);
      if (hit) consumed = true;
    }
    if (consumed) e.preventDefault();
    updateTouchState();
  }

  function onTouchMove(e) {
    let consumed = false;
    for (const t of e.changedTouches) {
      if (activeTouches.has(t.identifier)) {
        const hit = classifyTouch(t);
        activeTouches.set(t.identifier, hit);
        if (hit) consumed = true;
      }
    }
    if (consumed) e.preventDefault();
    updateTouchState();
  }

  function onTouchEnd(e) {
    for (const t of e.changedTouches) {
      activeTouches.delete(t.identifier);
    }
    updateTouchState();
  }

  if (isMobile) {
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd);
    canvas.addEventListener('touchcancel', onTouchEnd);
  }

  // Start button
  if (startBtn) {
    startBtn.addEventListener('click', function () {
      if (state === STATE.TITLE || state === STATE.FINISHED) {
        startRace();
      }
    });
    startBtn.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        startBtn.click();
      }
    });
  }

  // Show popup on title
  if (popUp) {
    popUp.classList.remove('hidden');
    popUp.setAttribute('aria-hidden', 'false');
  }

  function startRace() {
    canvas.classList.remove('no-interact');
    if (popUp) {
      popUp.classList.add('hidden');
      popUp.setAttribute('aria-hidden', 'true');
    }
    state = STATE.COUNTDOWN;
    countdownValue = 3;
    countdownTimer = performance.now();
    playerZ = 0;
    playerX = 0;
    speed = 0;
    lap = 1;
    lapTimes = [];
    bestLapTime = Infinity;
    currentLapTime = 0;
    lastLapZ = 0;
    isOffroad = false;
    commentary = '';
    finishedTimer = 0;
    finishedFade = 0;
  }

  function showCommentary(msg, duration) {
    commentary = msg;
    commentaryTimer = performance.now() + (duration || 2500);
  }

  function announce(text) {
    if (announcer) {
      announcer.textContent = text;
    }
  }

  // Main game loop
  let lastTime = performance.now();

  function gameLoop(timestamp) {
    const dt = Math.min((timestamp - lastTime) / 1000, 0.05); // cap delta
    lastTime = timestamp;

    resizeIfNeeded();
    update(dt, timestamp);
    draw(timestamp);

    if (document.getElementById('racingCanvas')) {
      requestAnimationFrame(gameLoop);
    } else {
      // Cleanup when canvas removed (AJAX nav)
      cleanup();
    }
  }

  let lastW = 0, lastH = 0;
  function resizeIfNeeded() {
    if (window.innerWidth !== lastW || window.innerHeight !== lastH) {
      lastW = window.innerWidth;
      lastH = window.innerHeight;
      resizeCanvas();
    }
  }

  function update(dt, timestamp) {
    switch (state) {
      case STATE.TITLE:
        titleScrollOffset += dt * 100;
        break;

      case STATE.COUNTDOWN:
        updateCountdown(timestamp);
        break;

      case STATE.RACING:
        updateRacing(dt, timestamp);
        break;

      case STATE.FINISHED: {
        // Cruise around the track at ~50mph
        const cruiseSpeed = MAX_SPEED * 0.25;
        if (speed > cruiseSpeed) {
          speed = Math.max(cruiseSpeed, speed - NATURAL_DECEL * dt);
        } else {
          speed = cruiseSpeed;
        }
        playerZ += speed * dt;

        // Gentle auto-steer to stay on track
        const segI = ((Math.floor(playerZ / track.segmentLength) % track.totalSegments) + track.totalSegments) % track.totalSegments;
        const seg = track.segments[segI];
        if (seg) {
          playerX += -seg.curve * 0.3 * (speed / MAX_SPEED) * dt;
        }
        playerX *= 0.98; // drift back toward center

        // Fade in results overlay
        if (finishedTimer > 0) {
          finishedFade = Math.min(1, (performance.now() - finishedTimer) / 1500);
        }
        break;
      }
    }
  }

  function updateCountdown(timestamp) {
    const elapsed = timestamp - countdownTimer;
    if (elapsed < 1000) {
      countdownValue = 3;
    } else if (elapsed < 2000) {
      countdownValue = 2;
    } else if (elapsed < 3000) {
      countdownValue = 1;
    } else if (elapsed < 3800) {
      countdownValue = 0; // "GO!"
    } else {
      state = STATE.RACING;
      raceStartTime = timestamp;
      lapStartTime = timestamp;
      showCommentary(COMMENTARY.START, 3000);
      announce('Race started! 3 laps around The Breakfast Circuit.');
    }
  }

  function updateRacing(dt, timestamp) {
    // Clear expired commentary
    if (commentaryTimer && timestamp > commentaryTimer) {
      commentary = '';
      commentaryTimer = 0;
    }

    // Input
    let accel = false;
    let brake = false;
    let steerInput = 0;

    // Keyboard
    if (keys['ArrowUp'] || keys['w'] || keys['W']) accel = true;
    if (keys['ArrowDown'] || keys['s'] || keys['S'] || keys[' ']) brake = true;
    if (keys['ArrowLeft'] || keys['a'] || keys['A']) steerInput = -1;
    if (keys['ArrowRight'] || keys['d'] || keys['D']) steerInput = 1;

    // On-screen pad (mobile): L/R = steer, A = accelerate, B = brake
    if (touchLeft) steerInput = -1;
    if (touchRight) steerInput = 1;
    if (touchLeft && touchRight) steerInput = 0;
    if (touchGas) accel = true;
    if (touchBrake) brake = true;

    // Store steering for renderer
    currentSteerInput = steerInput;

    // Detect offroad (wider road feel)
    isOffroad = Math.abs(playerX) > 0.38;

    // Speed update
    if (accel && !brake) {
      const maxSpd = isOffroad ? MAX_SPEED * OFFROAD_SPEED_MULT : MAX_SPEED;
      speed = Math.min(maxSpd, speed + ACCELERATION * dt);
    } else if (brake) {
      speed = Math.max(0, speed - BRAKING * dt);
    } else {
      speed = Math.max(0, speed - NATURAL_DECEL * dt);
    }

    // Extra drag offroad (only drag down to min speed, don't fight acceleration below cap)
    if (isOffroad) {
      const offroadMax = MAX_SPEED * OFFROAD_SPEED_MULT;
      if (speed > offroadMax) {
        speed = Math.max(offroadMax, speed - OFFROAD_DRAG * dt);
      }

      // Commentary for going offroad
      if (speed > 50 && Math.random() < 0.01) {
        const msgs = COMMENTARY.OFFROAD;
        showCommentary(msgs[Math.floor(Math.random() * msgs.length)], 2000);
      }
    }

    // Steering with lateral momentum (drifty feel)
    const speedFactor = speed / MAX_SPEED;
    const targetLateral = steerInput * STEER_RATE * speedFactor;

    // Centrifugal force from curves
    const segIdx = ((Math.floor(playerZ / track.segmentLength) % track.totalSegments) + track.totalSegments) % track.totalSegments;
    const currentSeg = track.segments[segIdx];
    let curvePush = 0;
    if (currentSeg) {
      curvePush = -currentSeg.curve * CENTRIFUGAL * speedFactor;
    }

    // Smooth lateral speed toward target (gives drift/slide feel)
    const grip = isOffroad ? 2 : 5; // less grip offroad = more sliding
    lateralSpeed += (targetLateral + curvePush - lateralSpeed) * grip * dt;

    // Apply lateral movement
    playerX += lateralSpeed * dt;

    // Soft clamp lateral position (kill momentum at boundary, no bounce)
    if (Math.abs(playerX) > 3) {
      playerX = Math.max(-3, Math.min(3, playerX));
      lateralSpeed = 0;
    }

    // Move forward
    playerZ += speed * dt;

    // Lap detection
    const currentLapZ = playerZ - lastLapZ;
    currentLapTime = timestamp - lapStartTime;

    if (currentLapZ >= track.trackLength) {
      // Lap complete
      lapTimes.push(currentLapTime);

      if (currentLapTime < bestLapTime) {
        bestLapTime = currentLapTime;
        showCommentary(COMMENTARY.BEST_LAP, 3000);
      } else {
        const msgs = COMMENTARY.LAP_COMPLETE;
        showCommentary(msgs[Math.floor(Math.random() * msgs.length)], 2500);
      }

      announce(`Lap ${lap} complete. Time: ${formatTimeForSpeech(currentLapTime)}`);

      lap++;
      lastLapZ = playerZ;
      lapStartTime = timestamp;
      currentLapTime = 0;

      if (lap > TOTAL_LAPS) {
        // Race finished
        state = STATE.FINISHED;
        totalRaceTime = timestamp - raceStartTime;
        finishedTimer = performance.now();
        finishedFade = 0;
        showCommentary(COMMENTARY.FINISH, 5000);
        announce(`Race complete! Total time: ${formatTimeForSpeech(totalRaceTime)}`);
      } else if (lap === TOTAL_LAPS) {
        showCommentary(COMMENTARY.FINAL_LAP, 3000);
      }
    }
  }

  // Results overlay drawn on canvas
  let raceAgainBtn = { x: 0, y: 0, w: 0, h: 0 };

  function drawFinishedOverlay(ctx, w, h) {
    if (finishedFade <= 0) return;

    ctx.save();
    const cx = w / 2;
    const isBig = w > 600;

    // Fade overlay
    ctx.fillStyle = `rgba(0,0,0,${finishedFade * 0.7})`;
    ctx.fillRect(0, 0, w, h);

    ctx.globalAlpha = finishedFade;
    ctx.textAlign = 'center';

    // Title
    const titleSize = isBig ? 36 : 24;
    ctx.font = `bold ${titleSize}px monospace`;
    ctx.fillStyle = '#8b2252';
    ctx.fillText('RACE COMPLETE', cx, h * 0.2);

    // Total time
    const totalSize = isBig ? 26 : 18;
    ctx.font = `bold ${totalSize}px monospace`;
    ctx.fillStyle = '#fff';
    ctx.fillText(formatTimeDisplay(totalRaceTime), cx, h * 0.2 + titleSize + 12);

    // Lap times
    const lapSize = isBig ? 16 : 12;
    const lapStartY = h * 0.2 + titleSize + totalSize + 32;
    const bestIdx = lapTimes.indexOf(Math.min(...lapTimes));
    ctx.font = `${lapSize}px monospace`;

    for (let i = 0; i < lapTimes.length; i++) {
      const isBest = i === bestIdx;
      ctx.fillStyle = isBest ? '#8b2252' : '#aaa';
      const label = `LAP ${i + 1}  ${formatTimeDisplay(lapTimes[i])}${isBest ? '  BEST' : ''}`;
      ctx.fillText(label, cx, lapStartY + i * (lapSize + 10));
    }

    // "RACE AGAIN" button
    if (finishedFade >= 1) {
      const btnW = isBig ? 220 : 160;
      const btnH = isBig ? 50 : 40;
      const btnX = cx - btnW / 2;
      const btnY = lapStartY + lapTimes.length * (lapSize + 10) + 30;
      const btnR = 6;

      // Store for hit testing
      raceAgainBtn = { x: btnX, y: btnY, w: btnW, h: btnH };

      // Button shadow
      ctx.fillStyle = '#1a1a1a';
      ctx.beginPath();
      ctx.roundRect(btnX, btnY + 3, btnW, btnH, btnR);
      ctx.fill();

      // Button face
      ctx.fillStyle = '#8b2252';
      ctx.beginPath();
      ctx.roundRect(btnX, btnY, btnW, btnH, btnR);
      ctx.fill();

      // Button text
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${isBig ? 18 : 14}px monospace`;
      ctx.textBaseline = 'middle';
      ctx.fillText('RACE AGAIN', cx, btnY + btnH / 2);
    }

    ctx.restore();
  }

  // Handle tap on "RACE AGAIN" button
  canvas.addEventListener('click', function (e) {
    if (state !== STATE.FINISHED || finishedFade < 1) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    if (x >= raceAgainBtn.x && x <= raceAgainBtn.x + raceAgainBtn.w &&
        y >= raceAgainBtn.y && y <= raceAgainBtn.y + raceAgainBtn.h) {
      startRace();
    }
  });

  function draw(timestamp) {
    const w = canvas.width;
    const h = canvas.height;

    switch (state) {
      case STATE.TITLE:
        renderTitleScreen(ctx, w, h, track, titleScrollOffset);
        break;

      case STATE.COUNTDOWN:
        renderScene(ctx, w, h, track, playerZ, playerX, speed, MAX_SPEED, isMobile, currentSteerInput);
        renderCountdown(ctx, w, h, countdownValue);
        break;

      case STATE.RACING:
        renderScene(ctx, w, h, track, playerZ, playerX, speed, MAX_SPEED, isMobile, currentSteerInput);
        renderHUD(ctx, w, h, {
          speed: speed * 0.02, // Scale to realistic MPH (max ~200)
          lap: Math.min(lap, TOTAL_LAPS),
          totalLaps: TOTAL_LAPS,
          currentLapTime,
          bestLapTime,
          lapTimes,
          commentary,
          minimapPoints,
          playerProgress: (playerZ % track.trackLength) / track.trackLength,
          isOffroad,
        });

        // Mobile control pad (Game Boy style: D-pad on the left, A/B on the right)
        if (isMobile) {
          drawControlPad(ctx, w, h);
        }

        break;

      case STATE.FINISHED:
        renderScene(ctx, w, h, track, playerZ, playerX, speed, MAX_SPEED, isMobile, currentSteerInput);
        drawFinishedOverlay(ctx, w, h);
        break;
    }
  }

  function drawControlPad(ctx, w, h) {
    const layout = getButtonLayout(w, h);
    const FACE = '#8b2252';
    const FACE_PRESSED = '#6a1b4d';
    const DPAD = '#2c4f8b';
    const DPAD_PRESSED = '#1f3a6a';
    const SHADOW = '#1a1a1a';

    function drawCircle(b, pressed) {
      // Shadow
      ctx.beginPath();
      ctx.arc(b.x, b.y + 3, b.r, 0, Math.PI * 2);
      ctx.fillStyle = SHADOW;
      ctx.fill();
      // Face
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
      ctx.fillStyle = pressed ? FACE_PRESSED : FACE;
      ctx.fill();
      // Highlight
      ctx.beginPath();
      ctx.arc(b.x, b.y - 4, b.r - 6, Math.PI, 0);
      ctx.fillStyle = pressed ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.12)';
      ctx.fill();
      // Label
      ctx.fillStyle = pressed ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.4)';
      ctx.font = 'bold 22px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(b.label, b.x, b.y + 1);
    }

    function drawRect(b, pressed) {
      const radius = 10;
      // Shadow
      roundRect(ctx, b.x, b.y + 3, b.w, b.h, radius);
      ctx.fillStyle = SHADOW;
      ctx.fill();
      // Face
      roundRect(ctx, b.x, b.y, b.w, b.h, radius);
      ctx.fillStyle = pressed ? DPAD_PRESSED : DPAD;
      ctx.fill();
      // Highlight strip
      roundRect(ctx, b.x + 6, b.y + 5, b.w - 12, 6, 3);
      ctx.fillStyle = pressed ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.16)';
      ctx.fill();
      // Label
      ctx.fillStyle = pressed ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.55)';
      ctx.font = 'bold 26px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(b.label, b.x + b.w / 2, b.y + b.h / 2 + 1);
    }

    ctx.save();
    drawRect(layout.L, touchLeft);
    drawRect(layout.R, touchRight);
    drawCircle(layout.B, touchBrake);
    drawCircle(layout.A, touchGas);
    ctx.restore();
  }

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  function cleanup() {
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('keyup', onKeyUp);
    gameBuilt = false;
  }

  // Start the loop
  requestAnimationFrame(gameLoop);
}

function formatTimeDisplay(ms) {
  if (ms === Infinity || ms === undefined) return '--:--.---';
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  const millis = Math.floor(ms % 1000);
  return `${mins}:${secs.toString().padStart(2, '0')}.${millis.toString().padStart(3, '0')}`;
}

function formatTimeForSpeech(ms) {
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  if (mins > 0) return `${mins} minutes ${secs} seconds`;
  return `${secs} seconds`;
}
